import React from 'react';
import { Link } from 'react-router-dom';
import AppsLayout from './AppsLayout';
import { PolicyArticle, PolicyContents } from './components/PolicyContent';
import Button from './components/Button';
import policy from '../../content/birdify/privacy.json';
import provenance from '../../content/birdify/privacy-source.json';
import { trackClick } from '../../firebase/analytics';
import { APPS_ROUTES } from './routes';

const PAGE = 'apps_birdify_privacy';

/**
 * Full Birdify privacy policy rendered from the checked-in snapshot.
 * Website-specific privacy statements live separately in the website privacy policy (/privacy).
 * No animation is applied to policy text.
 */
export default function BirdifyPrivacyPage() {
  const isDraft = policy.effectiveDate.isPlaceholder;

  return (
    <AppsLayout
      theme="canopy"
      title="Birdify privacy policy · Keval Langalia"
      description="The complete privacy policy for the Birdify Android app: recording and local identification, saved sessions, optional location, online providers, Google services, logs and deletion."
      canonicalPath={APPS_ROUTES.birdifyPrivacy}
      breadcrumbs={[
        { label: 'Portfolio', to: APPS_ROUTES.apps, id: 'apps' },
        { label: 'Birdify', to: APPS_ROUTES.birdify, id: 'birdify' },
        { label: 'Privacy policy' },
      ]}
      page={PAGE}
      support={false}
    >
      <header className="apps-section" style={{ marginTop: 0 }}>
        <p className="apps-eyebrow">{policy.app} · Privacy</p>
        <h1 className="apps-title">{policy.title}</h1>
        <dl className="apps-policy-meta">
          <div>
            <dt>Operator</dt>
            <dd>{policy.operator}</dd>
          </div>
          <div>
            <dt>Effective date</dt>
            <dd>
              {policy.effectiveDate.isPlaceholder ? (
                <em>Not yet effective — the date will be set when this policy is published.</em>
              ) : (
                policy.effectiveDate.text
              )}
            </dd>
          </div>
          <div>
            <dt>Privacy contact</dt>
            <dd>
              <a href={`mailto:${policy.contact.email}`} onClick={trackClick(PAGE, 'header_contact_email_link')}>{policy.contact.label}</a>
            </dd>
          </div>
        </dl>

        {isDraft ? (
          <div className="apps-notice" role="note" aria-labelledby="policy-draft-heading">
            <strong id="policy-draft-heading">Pre-publication draft</strong>
            <p>This page shows the prepared policy text so it can be reviewed in place. It is not yet published or effective.</p>
          </div>
        ) : null}
      </header>

      <div className="apps-policy">
        <PolicyContents policy={policy} />

        <div>
          <PolicyArticle policy={policy} appName="Birdify">
            <p className="apps-muted" style={{ marginTop: '2rem', fontSize: '0.9rem' }}>
              Policy text imported from the Birdify repository ({provenance.sourcePath} at commit {provenance.sourceCommit.slice(0, 12)}). Only the document layout differs from the source.
            </p>
          </PolicyArticle>

          <aside className="apps-notice apps-notice--info" style={{ marginTop: '2rem' }} aria-label="Website privacy policy note">
            <strong>Website privacy</strong>
            <p>
              This policy applies to the Birdify Android app. For information on how this website (keval.io) uses optional analytics, cookies and browser storage, please review the{' '}
              <Link to="/privacy" className="kds-link" onClick={trackClick(PAGE, 'website_privacy_link')}>
                Website privacy policy
              </Link>.
            </p>
          </aside>

          <div className="apps-hero-actions" style={{ marginTop: '2rem' }}>
            <Button to={APPS_ROUTES.birdify} variant="secondary" onClick={trackClick(PAGE, 'back_to_birdify_button')}>
              Back to Birdify
            </Button>
            <Link to={APPS_ROUTES.apps} className="kds-btn kds-btn--text" onClick={trackClick(PAGE, 'all_apps_link')}>
              All projects
            </Link>
          </div>
        </div>
      </div>
    </AppsLayout>
  );
}
